import useWebSocket from "Hooks/useWebSocket";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { initialState, reducer } from "Reducer/GameReducer";
import { gameContextInitial, gameContextReducer } from "Reducer/ContextReducer";
import { AppContext } from "Game/AppContext";
import { animationInitial, animationReducer } from "Reducer/AnimationReducer";

// ─── Split Contexts ──────────────────────────────────────────
// VonderGOContext  เดิมมี ~50 properties → consumer ทุกตัวรีเรนเดอร์ตามกันหมด
// แยกเป็น 3 context ตามความถี่การเปลี่ยนแปลง:
//   AnimationContext — เปลี่ยนบ่อยสุด (animationState ทุก frame)
//   GameDataContext  — เปลี่ยนทุก phase (gameState, gameData, leaderboard)
//   VonderGOContext  — เปลี่ยนน้อย (session, websocket, endUsers)

const VonderGOContext = createContext();
const AnimationContext = createContext();
const GameDataContext = createContext();

const initialEndUser = [];
const initialAllowCharacters = [];
const initialPhase = { phase: null };
const initialSettings = {
  allowDoubt: false,
  conditionCurrency: 0,
  conditionPercent: 0,
  maxLeaderboardPlayer: 0,
  shuffleAnswers: false,
  shuffleQuestions: false,
};

const VonderGOProvider = ({ children }) => {
  const appContextData = useContext(AppContext);

  // Stable refs to AppContext functions – break circular re-render chain
  const appCtxRef = useRef(appContextData);
  appCtxRef.current = appContextData;

  const removeEndUsersFromApp = useCallback((userID) => {
    appCtxRef.current.removeEndUsers(userID);
  }, []);
  const updateEndUsersInApp = useCallback((userID, content) => {
    appCtxRef.current.updateEndUsers(userID, content);
  }, []);
  const sessionStartInApp = useCallback((topEndUser) => {
    appCtxRef.current.sessionStart(topEndUser);
  }, []);

  const [pin, setPIN] = useState(null);
  const [uuid, setUUID] = useState(null);
  const [connectionID, setConnectionID] = useState(null);
  const [challengeID, setChallengeID] = useState(null);
  const [challengeCode, setChallengeCode] = useState(null);
  const [challengeImage, setChallengeImage] = useState(null);
  const [challengeContext, setChallengeContext] = useState(null);
  const [isReadyToPlay, setIsReadyToPlay] = useState(false);
  const [endSessionLoading, setEndSessionLoading] = useState(false);
  const [gameContextState, dispatchGameContextState] = useReducer(
    gameContextReducer,
    gameContextInitial
  );
  const [totalUsers, setTotalUsers] = useState(0);
  const [userLike, setUserLike] = useState(null);
  const [isExit, setIsExit] = useState(false);
  const [currentGameData, setCurrentGameData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [qaList, setQAList] = useState([]);
  const [level, setLevel] = useState(null);
  const [maxUser, setMaxUser] = useState(0);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [currentGameState, dispatchGameState] = useReducer(
    reducer,
    initialState
  );
  const [currentPhase, setCurrentPhase] = useState(null);
  const [animationState, dispatchAnimationState] = useReducer(
    animationReducer,
    animationInitial
  );
  const [wsReadyState, wsConnect, wsSend, wsClose, wsOn] = useWebSocket();

  const [isJumpQuestionIndexFromDropDown, setIsJumpQuestionIndexFromDropDown] =
    useState(false);
  const reducerAllowCharacters = (state, action) => {
    return [...action(state)];
  };

  const [allowCharacters, dispatchAllowCharacters] = useReducer(
    reducerAllowCharacters,
    initialAllowCharacters
  );

  const setAllowCharacters = useCallback((characters) => {
    dispatchAllowCharacters(() => {
      return [...characters];
    });
  }, []);

  const clearAllowCharacters = useCallback(() => {
    dispatchAllowCharacters(() => {
      return [];
    });
  }, []);

  const reducerEndUsers = (state, action) => {
    return [...action(state)];
  };

  const [endUsers, dispatchEndUsers] = useReducer(
    reducerEndUsers,
    initialEndUser
  );

  const reducerPhase = (state, action) => {
    return { ...action(state) };
  };

  const [phase, dispatchPhase] = useReducer(reducerPhase, initialPhase);

  const reducerSettings = (state, action) => {
    return { ...action(state) };
  };

  const [settings, dispatchSettings] = useReducer(
    reducerSettings,
    initialSettings
  );

  const updateSettings = useCallback((newSettings) => {
    dispatchSettings((state) => {
      return { ...state, ...newSettings };
    });
  }, []);

  const addEndUser = useCallback((userID) => {
    let isSuccess = false;
    dispatchEndUsers((endUsers) => {
      const index = endUsers.findIndex((member) => {
        if (member.userID === userID) {
          member.isConnect = true;
        }
        return member.userID === userID;
      });
      if (index < 0) {
        endUsers.push({ userID });
        isSuccess = true;
      }
      return endUsers;
    });
    return isSuccess;
  }, []);

  const removeEndUser = useCallback(
    (userID) => {
      let isSuccess = false;
      dispatchEndUsers((endUsers) => {
        const index = endUsers.findIndex((member) => {
          return member.userID === userID;
        });
        if (index >= 0) {
          delete endUsers[index];
          endUsers.splice(index, 1);
          isSuccess = true;
        }
        return endUsers;
      });
      removeEndUsersFromApp(userID);
      return isSuccess;
    },
    [removeEndUsersFromApp]
  );
  const updateEndUser = useCallback(
    (userID, content) => {
      let isSuccess = false;
      dispatchEndUsers((endUsers) => {
        const index = endUsers.findIndex((member) => {
          return member.userID === userID;
        });
        console.log("index from remove", index);
        if (index >= 0) {
          endUsers[index] = { ...endUsers[index], ...content };
          isSuccess = true;
        }
        return endUsers;
      });
      updateEndUsersInApp(userID, content);
      return isSuccess;
    },
    [updateEndUsersInApp]
  );
  const disconnectEndUser = useCallback((userID) => {
    let isSuccess = false;
    dispatchEndUsers((endUsers) => {
      const index = endUsers.findIndex((member) => {
        return member.userID === userID;
      });
      if (index >= 0) {
        endUsers[index] = { ...endUsers[index], isConnect: false };
      }
      return endUsers;
    });
    return isSuccess;
  }, []);

  const sessionStart = useCallback(
    (topEndUser) => {
      sessionStartInApp(topEndUser);
    },
    [sessionStartInApp]
  );

  // Sync state to AppContext (using refs to avoid dependency on unstable appContextData object)
  useEffect(() => {
    if (currentGameState) {
      appCtxRef.current.setCurrentGameState(currentGameState);
    }
  }, [currentGameState]);

  useEffect(() => {
    if (animationState) {
      appCtxRef.current.setAnimationState(animationState);
    }
  }, [animationState]);

  useEffect(() => {
    if (currentGameData) {
      appCtxRef.current.setCurrentGameData(currentGameData);
    }
  }, [currentGameData]);

  useEffect(() => {
    if (gameContextState) {
      appCtxRef.current.setGameContextState(gameContextState);
    }
  }, [gameContextState]);

  // Stable wrappers for AppContext-derived values (using ref to avoid re-render chain)
  const resetGameSession = useCallback(() => {
    appCtxRef.current.resetGameSession();
  }, []);
  const playSFX = useCallback((name) => {
    appCtxRef.current.playSFX(name);
  }, []);
  const onPhaseUpdateData = useCallback((phaseData) => {
    appCtxRef.current.onPhaseUpdate(phaseData);
  }, []);
  const isCharacterReady = appCtxRef.current.isCharacterReady;

  // ─── AnimationContext ──────────────────────────────────────
  // เปลี่ยนบ่อยสุด — แยกออกมาให้ consumer ที่ไม่ใช้ animation ไม่ต้องรีเรนเดอร์
  const animationValue = useMemo(() => ({
    animationState,
    dispatchAnimationState,
  }), [animationState]);

  // ─── GameDataContext ───────────────────────────────────────
  // เปลี่ยนทุก phase — currentGameState, currentGameData, leaderboardData, gameContextState
  const gameDataValue = useMemo(() => ({
    currentGameState,
    dispatchGameState,
    currentGameData,
    setCurrentGameData,
    leaderboardData,
    setLeaderboardData,
    currentPhase,
    setCurrentPhase,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    gameContextState,
    dispatchGameContextState,
  }), [
    currentGameState, currentGameData, leaderboardData,
    currentPhase, currentQuestionIndex, gameContextState,
  ]);

  // ─── VonderGOContext (slimmed) ─────────────────────────────
  // Session, WebSocket, endUsers — เปลี่ยนไม่บ่อย เทียบกับ animation/gameData
  const providerValue = useMemo(() => ({
    pin,
    setPIN,
    uuid,
    setUUID,
    wsReadyState,
    wsConnect,
    wsSend,
    wsClose,
    wsOn,
    connectionID,
    setConnectionID,
    challengeID,
    setChallengeID,
    challengeCode,
    setChallengeCode,
    challengeContext,
    setChallengeContext,
    challengeImage,
    setChallengeImage,
    isReadyToPlay,
    setIsReadyToPlay,
    allowCharacters,
    setAllowCharacters,
    clearAllowCharacters,
    endUsers,
    addEndUser,
    removeEndUser,
    updateEndUser,
    disconnectEndUser,
    level,
    setLevel,
    phase,
    dispatchPhase,
    settings,
    updateSettings,
    dispatchSettings,
    totalUsers,
    setTotalUsers,
    endSessionLoading,
    setEndSessionLoading,
    userLike,
    setUserLike,
    qaList,
    setQAList,
    sessionStart,
    resetGameSession,
    playSFX,
    onPhaseUpdateData,
    isCharacterReady,
    isExit,
    setIsExit,
    showMediaModal,
    setShowMediaModal,
    maxUser,
    setMaxUser,
    isJumpQuestionIndexFromDropDown,
    setIsJumpQuestionIndexFromDropDown,
  }), [
    pin, uuid, wsReadyState, wsConnect, wsSend, wsClose, wsOn,
    connectionID, challengeID, challengeCode, challengeContext,
    challengeImage, isReadyToPlay, allowCharacters, setAllowCharacters,
    clearAllowCharacters, endUsers, addEndUser, removeEndUser,
    updateEndUser, disconnectEndUser, level, phase, settings,
    updateSettings, totalUsers, endSessionLoading, userLike, qaList,
    sessionStart, resetGameSession, playSFX, onPhaseUpdateData,
    isCharacterReady, isExit, showMediaModal, maxUser,
    isJumpQuestionIndexFromDropDown,
  ]);

  return (
    <VonderGOContext.Provider value={providerValue}>
      <GameDataContext.Provider value={gameDataValue}>
        <AnimationContext.Provider value={animationValue}>
          {children}
        </AnimationContext.Provider>
      </GameDataContext.Provider>
    </VonderGOContext.Provider>
  );
};

const useVonderGoContext = () => {
  const context = useContext(VonderGOContext);
  if (context === undefined)
    throw new Error(
      "Vonder GO Context is undefined, maybe called from outer provider scope."
    );
  return context;
};

const useAnimationContext = () => {
  const context = useContext(AnimationContext);
  if (context === undefined)
    throw new Error(
      "Animation Context is undefined, maybe called from outer provider scope."
    );
  return context;
};

const useGameDataContext = () => {
  const context = useContext(GameDataContext);
  if (context === undefined)
    throw new Error(
      "GameData Context is undefined, maybe called from outer provider scope."
    );
  return context;
};

export {
  VonderGOContext,
  VonderGOProvider,
  useVonderGoContext,
  AnimationContext,
  useAnimationContext,
  GameDataContext,
  useGameDataContext,
};
